// exp_PORTFOLIO-61587_file
import { FC, MouseEvent, useCallback, useMemo } from 'react';

import { push } from 'connected-react-router';
import {
    CampaignCreationWizardStep,
    createCampaignCreationWizardBreadcrumbClickHandler,
} from 'utils/campaignCreationWizardFlow/campaignCreationWizardFlow';
import { CREATE_MENU_PAGE_ROUTE, DASHBOARD_PAGE_ROUTE } from 'utils/constants/app';
import { CampaignProductType } from 'utils/constants/campaign';

import useTrls from 'hooks/translation/useTrls';
import { useCampaignCreationWizardBackAlert } from 'hooks/useCampaignCreationWizardBackAlert';
import { useCampaignCreationWizardStepNavigation } from 'hooks/useCampaignCreationWizardStepNavigation';

import styles from 'pages/VacancyOfTheDayPage/VacancyOfTheDayPage.module.less';

import { VacancyOfTheDayBuilder } from 'components/VacancyOfTheDayBuilder';

import { PageContentHeader } from 'components/common/PageContentHeader/PageContentHeader';
import { VacancyLink } from 'components/common/VacancyLink/VacancyLink';
import { Header } from 'components/Header/Header';

import { selectVacancyOfTheDayVacancy } from 'store/models/vacancyOfTheDay';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'VacancyOfTheDayPage.title',
    breadcrumbs: {
        main: 'CampaignCreationWizard.breadcrumbs.main',
        creation: 'CampaignCreationWizard.breadcrumbs.creation',
        promotionType: 'CampaignCreationWizard.breadcrumbs.promotionType',
    },
};

export const VacancyOfTheDayPage: FC = () => {
    const { vacancyId, vacancyName } = useSelector(selectVacancyOfTheDayVacancy);
    const dispatch = useDispatch();
    const trls = useTrls();
    const { backAlert, openBackAlert } = useCampaignCreationWizardBackAlert();
    const goToWizardStep = useCampaignCreationWizardStepNavigation();
    const isWizardFlowEnabled = Boolean(vacancyId);

    const openWizardBackAlert = useCallback(
        (activeStep: CampaignCreationWizardStep) => {
            openBackAlert(() => {
                goToWizardStep(activeStep, {
                    vacancyId,
                    vacancyName: vacancyName ?? undefined,
                    productType: CampaignProductType.VacancyOfTheDay,
                });
            });
        },
        [goToWizardStep, openBackAlert, vacancyId, vacancyName]
    );

    const additionalContent = vacancyId ? (
        <VacancyLink dataQa="header-vacancy-link" vacancyId={vacancyId} vacancyName={vacancyName} />
    ) : null;

    const breadcrumbs = useMemo(() => {
        if (isWizardFlowEnabled) {
            return [
                {
                    to: DASHBOARD_PAGE_ROUTE,
                    children: trls(TrlKeys.breadcrumbs.main),
                    onClick: (event: MouseEvent) => {
                        event.preventDefault();
                        openBackAlert(() => dispatch(push(DASHBOARD_PAGE_ROUTE)));
                    },
                },
                {
                    to: CREATE_MENU_PAGE_ROUTE,
                    children: trls(TrlKeys.breadcrumbs.creation),
                    onClick: createCampaignCreationWizardBreadcrumbClickHandler(
                        openWizardBackAlert,
                        CampaignCreationWizardStep.PromotionObject
                    ),
                },
                {
                    to: CREATE_MENU_PAGE_ROUTE,
                    children: trls(TrlKeys.breadcrumbs.promotionType),
                    onClick: createCampaignCreationWizardBreadcrumbClickHandler(
                        openWizardBackAlert,
                        CampaignCreationWizardStep.PromotionType
                    ),
                },
                { to: '#', children: trls(TrlKeys.title) },
            ];
        }

        return [
            { to: CREATE_MENU_PAGE_ROUTE, children: trls(TrlKeys.breadcrumbs.main) },
            { to: '#', children: trls(TrlKeys.title) },
        ];
    }, [dispatch, isWizardFlowEnabled, openBackAlert, openWizardBackAlert, trls]);

    return (
        <div className={styles.wrapper}>
            <Header />
            <PageContentHeader
                breadcrumbsConfig={breadcrumbs}
                title={trls(TrlKeys.title)}
                additionalContent={additionalContent}
                vSpacingBottom={24}
            />
            <VacancyOfTheDayBuilder />
            {isWizardFlowEnabled && backAlert}
        </div>
    );
};
